"use client";

import React, { createContext, useContext, useCallback, useEffect, useRef, useState, ReactNode } from "react";
import { API_BASE, fetchWithAuth } from "@/lib/auth";
import { useWorkspace } from "@/lib/context/WorkspaceContext";

type SeenMap = Record<string, string>;

/**
 * Reserved id for the global "don't show me tours" opt-out. It rides the same
 * `users.tours_seen` map (and satisfies the backend's TOUR_ID_RE), so opting
 * out needs no new column, endpoint or migration. Never use it as a real tour.
 */
export const SUPPRESS_ALL_ID = "_suppress_all";

interface TourState {
    /** null while loading or when unauthenticated — auto-tours stay disabled. */
    seen: SeenMap | null;
    isLoaded: boolean;
    hasSeen: (tourId: string) => boolean;
    markSeen: (tourId: string) => void;
    /** User asked never to be auto-toured again. Replay still works. */
    suppressed: boolean;
    suppressAll: () => void;
}

const TourContext = createContext<TourState | undefined>(undefined);

const cacheKey = (userId: string) => `tours_seen_cache_v1:${userId}`;

function readCache(userId: string): SeenMap | null {
    try {
        const raw = localStorage.getItem(cacheKey(userId));
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

function writeCache(userId: string, seen: SeenMap) {
    try {
        localStorage.setItem(cacheKey(userId), JSON.stringify(seen));
    } catch {
        /* storage full/blocked — cache is best-effort */
    }
}

export function TourProvider({ children }: { children: ReactNode }) {
    const { user, isLoading } = useWorkspace();
    const [seen, setSeen] = useState<SeenMap | null>(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const fetchedFor = useRef<string | null>(null);

    useEffect(() => {
        if (isLoading) return;
        if (!user) {
            fetchedFor.current = null;
            setSeen(null);
            setIsLoaded(true);
            return;
        }
        if (fetchedFor.current === user.id) return;
        fetchedFor.current = user.id;

        // Cache fast-path so returning users on slow networks never see a tour flash.
        const cached = readCache(user.id);
        if (cached) {
            setSeen(cached);
            setIsLoaded(true);
        }

        (async () => {
            try {
                const res = await fetchWithAuth(`${API_BASE}/api/v1/tours/seen`);
                if (!res.ok) throw new Error(`tours/seen ${res.status}`);
                const data = await res.json();
                const serverSeen: SeenMap = data.seen || {};
                setSeen(serverSeen);
                writeCache(user.id, serverSeen);
            } catch (err) {
                console.error("Failed to load guided-tour state", err);
                // Without the backend map, fall back to cache (already set) or
                // disable auto-tours rather than re-showing everything.
                if (!cached) setSeen(null);
            } finally {
                setIsLoaded(true);
            }
        })();
    }, [user, isLoading]);

    const hasSeen = useCallback((tourId: string) => !!seen && tourId in seen, [seen]);

    const markSeen = useCallback(
        (tourId: string) => {
            if (!user) return;
            setSeen((prev) => {
                const next = { ...(prev || {}), [tourId]: new Date().toISOString() };
                writeCache(user.id, next);
                return next;
            });
            fetchWithAuth(`${API_BASE}/api/v1/tours/seen`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ tour_id: tourId }),
            }).catch((err) => console.error("Failed to persist tour state", err));
        },
        [user]
    );

    const suppressAll = useCallback(() => markSeen(SUPPRESS_ALL_ID), [markSeen]);
    const suppressed = hasSeen(SUPPRESS_ALL_ID);

    return (
        <TourContext.Provider value={{ seen, isLoaded, hasSeen, markSeen, suppressed, suppressAll }}>
            {children}
        </TourContext.Provider>
    );
}

export function useTours(): TourState {
    const ctx = useContext(TourContext);
    if (ctx === undefined) {
        throw new Error("useTours must be used within a TourProvider");
    }
    return ctx;
}
